import React, { useState, useEffect } from 'react';
import { CreditCard, Calendar, AlertTriangle, CheckCircle, XCircle, RefreshCw, Crown } from 'lucide-react';
import { subscriptionAPI, Subscription, SubscriptionStatus, StripeProduct } from '../../services/subscriptionAPI';
import CardManagement from './CardManagement';

interface SubscriptionManagementProps {
  siteId: string;
  isSuperAdmin?: boolean;
}

const SubscriptionManagement: React.FC<SubscriptionManagementProps> = ({ 
  siteId, 
  isSuperAdmin = false 
}) => {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'overview' | 'payment' | 'billing'>('overview');
  const [processing, setProcessing] = useState(false);
  const [products, setProducts] = useState<StripeProduct[]>([]);
  const [selectedPriceIds, setSelectedPriceIds] = useState<string[]>([]);

  useEffect(() => {
    loadSubscriptionData();
  }, [siteId]);

  const loadSubscriptionData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const productResponse = await subscriptionAPI.getProducts(siteId);
      setProducts(productResponse.data.products);
      if (productResponse.data.configuredBasePriceId) {
        setSelectedPriceIds([productResponse.data.configuredBasePriceId]);
      }

      // First try to get subscription status
      const statusResponse = await subscriptionAPI.getSubscriptionStatus(siteId);
      setSubscriptionStatus(statusResponse.data);
      
      // If subscription exists, get full details
      if (statusResponse.data.hasSubscription) {
        const subscriptionResponse = await subscriptionAPI.getSubscription(siteId);
        setSubscription(subscriptionResponse.data);
        setSelectedPriceIds(subscriptionResponse.data.items.map(item => item.stripePriceId));
      } else {
        setSubscription(null);
      }
    } catch (error: any) {
      console.error('Subscription load error:', error);
      if (error.response?.status === 403) {
        setError('Access denied. You do not have permission to manage billing for this site.');
      } else if (error.response?.status === 404) {
        // No subscription exists - this is normal
        setSubscription(null);
        const statusResponse = await subscriptionAPI.getSubscriptionStatus(siteId);
        setSubscriptionStatus(statusResponse.data);
      } else {
        setError(error.response?.data?.error || 'Failed to load subscription data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel this subscription? This will take effect at the end of the current billing period.')) {
      return;
    }

    try {
      setProcessing(true);
      await subscriptionAPI.cancelSubscription(siteId, true);
      setSuccess('Subscription canceled successfully');
      loadSubscriptionData();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to cancel subscription');
    } finally {
      setProcessing(false);
    }
  };

  const handleCreateSubscription = async (startTrial = false) => {
    if (!selectedPriceIds.length) {
      setError('Select at least one product.');
      return;
    }
    // First check if there are any payment methods
    try {
      const paymentMethodsResponse = await subscriptionAPI.getPaymentMethods(siteId);
      const paymentMethods = paymentMethodsResponse.data.paymentMethods || [];
      
      if (paymentMethods.length === 0 && !startTrial) {
        // No payment methods - redirect to payment tab and show message
        setActiveTab('payment');
        setError('Please add a payment method first, then create a subscription.');
        return;
      }
      
      // Has payment methods - create subscription with default one
      const defaultPaymentMethodId = paymentMethodsResponse.data.defaultPaymentMethodId || paymentMethods[0]?.stripePaymentMethodId;
      
      setProcessing(true);
      await subscriptionAPI.createSubscription({
        siteId,
        paymentMethodId: defaultPaymentMethodId,
        priceIds: selectedPriceIds,
        startTrial
      });
      
      setSuccess(startTrial ? 'Your 14-day free trial has started!' : 'Subscription created successfully!');
      loadSubscriptionData();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to create subscription');
    } finally {
      setProcessing(false);
    }
  };

  const toggleProduct = (priceId: string) => {
    const product = products.find(item => item.priceId === priceId);
    if (product?.isSoftwareSubscription && !isSuperAdmin) return;
    if (product?.isSoftwareSubscription && isSuperAdmin) {
      const softwarePriceIds = new Set(products.filter(item => item.isSoftwareSubscription).map(item => item.priceId));
      setSelectedPriceIds(current => [
        ...current.filter(id => !softwarePriceIds.has(id)),
        priceId
      ]);
      return;
    }
    setSelectedPriceIds(current => current.includes(priceId)
      ? current.filter(id => id !== priceId)
      : [...current, priceId]);
  };

  const handleSaveProducts = async () => {
    if (!selectedPriceIds.length) {
      setError('A subscription must contain at least one product.');
      return;
    }
    try {
      setProcessing(true);
      setError('');
      await subscriptionAPI.updateSubscription(siteId, selectedPriceIds);
      setSuccess(subscriptionStatus?.status === 'trialing'
        ? 'Trial products updated. Billing will begin when the trial ends.'
        : 'Subscription products updated. Stripe will apply any prorations.');
      await loadSubscriptionData();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to update subscription products');
    } finally {
      setProcessing(false);
    }
  };

  const productSelector = (
    <div className="bg-white rounded-lg shadow-sm border p-6 text-left">
      <h3 className="text-lg font-medium text-gray-900 mb-1">Choose subscription products</h3>
      <p className="text-sm text-gray-600 mb-4">Add or remove modules. All selected products must share the same billing interval.</p>
      <div className="space-y-3">
        {products.map(product => (
          <label key={product.priceId} className={`flex items-center justify-between border rounded-lg p-4 ${product.isSoftwareSubscription && !isSuperAdmin ? 'bg-gray-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'}`}>
            <div className="flex items-center gap-3">
              <input type="checkbox" checked={selectedPriceIds.includes(product.priceId)} onChange={() => toggleProduct(product.priceId)} disabled={product.isSoftwareSubscription && !isSuperAdmin} className="h-4 w-4" />
              <div>
                <p className="font-medium text-gray-900">
                  {product.name}
                  {product.isSoftwareSubscription && !isSuperAdmin && <span className="ml-2 text-xs font-normal text-gray-500">Set by Super Admin</span>}
                </p>
                {product.description && <p className="text-sm text-gray-500">{product.description}</p>}
              </div>
            </div>
            <span className="font-medium whitespace-nowrap ml-4">
              {new Intl.NumberFormat('en-GB', { style: 'currency', currency: product.currency }).format(product.amount)} / {product.interval}
            </span>
          </label>
        ))}
      </div>
    </div>
  );

  const handleReactivateSubscription = async () => {
    if (!window.confirm('Are you sure you want to reactivate this subscription?')) {
      return;
    }

    try {
      setProcessing(true);
      // This would be a custom endpoint to reactivate
      setSuccess('Subscription reactivated successfully');
      loadSubscriptionData();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to reactivate subscription');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'trialing':
        return 'bg-blue-100 text-blue-800';
      case 'past_due':
        return 'bg-yellow-100 text-yellow-800';
      case 'canceled':
        return 'bg-red-100 text-red-800';
      case 'unpaid':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4" />;
      case 'trialing':
        return <Crown className="w-4 h-4" />;
      case 'past_due':
        return <AlertTriangle className="w-4 h-4" />;
      case 'canceled':
        return <XCircle className="w-4 h-4" />;
      case 'unpaid':
        return <XCircle className="w-4 h-4" />;
      default:
        return <AlertTriangle className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const subscribedProducts = (subscription?.items || []).map(item => {
    const catalogueProduct = products.find(product => product.priceId === item.stripePriceId);
    return {
      ...item,
      name: catalogueProduct?.name || item.description || 'Subscription product',
      description: catalogueProduct?.description || '',
      interval: catalogueProduct?.interval || 'month',
      intervalCount: catalogueProduct?.intervalCount || 1
    };
  });

  const subscriptionCurrency = subscribedProducts[0]?.currency || 'GBP';
  const subscriptionTotal = subscribedProducts.reduce(
    (total, item) => total + (item.amount || 0) * (item.quantity || 1),
    0
  );

  const formatMoney = (amount: number, currency: string = subscriptionCurrency) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(amount);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Subscription Details</h2>
          <p className="text-gray-600">Manage your subscription plan, payment methods, and billing settings</p>
        </div>
        <button
          onClick={loadSubscriptionData}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <p className="text-green-800">{success}</p>
          </div>
        </div>
      )}

      {/* No Subscription State */}
      {!subscriptionStatus?.hasSubscription ? (
        <div className="space-y-4">
        {productSelector}
        <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
          <CreditCard className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Subscription</h3>
          <p className="text-gray-600 mb-4">
            Set up a subscription to enable premium features and automated billing
          </p>
          <div className="flex flex-wrap justify-center gap-3">
          <button
            onClick={() => handleCreateSubscription(true)}
            disabled={processing || !selectedPriceIds.length}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {processing ? 'Starting...' : 'Start 14-day free trial'}
          </button>
          <button 
            onClick={() => handleCreateSubscription(false)}
            disabled={processing}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {processing ? 'Creating...' : 'Create Subscription'}
          </button>
          </div>
          <p className="text-xs text-gray-500 mt-3">No card is required for the trial. Add one before it ends to continue automatically; otherwise Stripe cancels the subscription.</p>
        </div>
        </div>
      ) : (
        <>
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('overview')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'overview'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('payment')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'payment'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Payment Methods
              </button>
              <button
                onClick={() => setActiveTab('billing')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'billing'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Billing History
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Subscription Status */}
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Subscription Status</h3>
                
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <span className={`inline-flex items-center px-3 py-1 text-sm rounded-full ${getStatusColor(subscriptionStatus.status)}`}>
                      {getStatusIcon(subscriptionStatus.status)}
                      <span className="ml-1 capitalize">{subscriptionStatus.status?.replace('_', ' ')}</span>
                    </span>
                    {subscriptionStatus.cancelAtPeriodEnd && (
                      <span className="text-sm text-gray-600">
                        Cancels on {formatDate(subscriptionStatus.nextBillingDate || '')}
                      </span>
                    )}
                  </div>
                  
                  {isSuperAdmin && (
                    <div className="flex items-center space-x-2">
                      {subscriptionStatus.cancelAtPeriodEnd ? (
                        <button
                          onClick={handleReactivateSubscription}
                          disabled={processing}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                        >
                          Reactivate
                        </button>
                      ) : (
                        <button
                          onClick={handleCancelSubscription}
                          disabled={processing}
                          className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50"
                        >
                          Cancel Subscription
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {subscriptionStatus.status === 'trialing' && subscriptionStatus.trialEnd && (
                  <div className="mb-4 rounded-lg bg-blue-50 border border-blue-200 p-4 text-blue-900">
                    Free trial ends on {formatDate(subscriptionStatus.trialEnd)}. Add a payment method before this date to continue on the selected paid products.
                  </div>
                )}

                {/* Billing Period */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Current Period</p>
                    <p className="font-medium">
                      {formatDate(subscription?.currentPeriodStart || '')} - {formatDate(subscription?.currentPeriodEnd || '')}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Next Billing Date</p>
                    <p className="font-medium">
                      {formatDate(subscriptionStatus.nextBillingDate || '')}
                    </p>
                  </div>
                </div>

                {/* Full subscription breakdown */}
                <div className="mt-6 border-t pt-6">
                  <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
                    <div>
                      <h4 className="font-medium text-gray-900">Subscribed products</h4>
                      <p className="text-xs text-gray-500 mt-1">Stripe subscription {subscription?.stripeSubscriptionId}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Recurring total</p>
                      <p className="text-2xl font-semibold text-primary-600">
                        {formatMoney(subscriptionTotal)}
                        <span className="text-sm font-normal text-gray-500"> / {subscribedProducts[0]?.interval || 'month'}</span>
                      </p>
                    </div>
                  </div>

                  {subscribedProducts.length ? (
                    <div className="divide-y border rounded-lg">
                      {subscribedProducts.map(item => (
                        <div key={item.stripePriceId} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4">
                          <div>
                            <p className="font-medium text-gray-900">{item.name}</p>
                            {item.description && <p className="text-sm text-gray-600 mt-1">{item.description}</p>}
                            <p className="text-xs text-gray-400 mt-1">Price ID: {item.stripePriceId}</p>
                          </div>
                          <div className="sm:text-right whitespace-nowrap">
                            <p className="font-medium text-gray-900">
                              {formatMoney(item.amount, item.currency)} / {item.interval}
                            </p>
                            {(item.quantity || 1) > 1 && (
                              <p className="text-sm text-gray-500">Quantity {item.quantity}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No subscription products were returned by Stripe.</p>
                  )}

                  {subscriptionStatus.status === 'incomplete' && (
                    <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
                      Payment setup is incomplete. Confirm the outstanding Stripe payment to activate these products.
                    </div>
                  )}
                </div>
              </div>

              {/* Pricing Details */}
              {subscription?.pricing && (
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Pricing Details</h3>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Base Price</span>
                      <span className="font-medium">
                        {subscription.pricing.basePrice.currency} {subscription.pricing.basePrice.amount.toFixed(2)}
                      </span>
                    </div>
                    
                    {subscription.items.map((item, index) => (
                      <div key={index} className="flex justify-between text-sm">
                        <span className="text-gray-600">{item.description}</span>
                        <span className="font-medium">
                          {item.currency} {item.amount.toFixed(2)}
                        </span>
                      </div>
                    ))}
                    
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Tax ({subscription.pricing.tax.rate}%)</span>
                      <span className="font-medium">
                        {subscription.pricing.basePrice.currency} {subscription.pricing.tax.amount.toFixed(2)}
                      </span>
                    </div>
                    
                    <div className="border-t pt-3">
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-900">Total</span>
                        <span className="font-medium text-lg text-primary-600">
                          {subscription.pricing.basePrice.currency} {subscription.pricing.totalAmount.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Default Payment Method */}
              {subscription?.defaultPaymentMethodId && (
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Default Payment Method</h3>
                  
                  {(() => {
                    const defaultMethod = subscription.paymentMethods.find(
                      method => method.stripePaymentMethodId === subscription.defaultPaymentMethodId
                    );
                    return defaultMethod ? (
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-gray-100 rounded-lg">
                          <CreditCard className="w-5 h-5 text-gray-600" />
                        </div>
                        <div>
                          <p className="font-medium capitalize">
                            {defaultMethod.brand} ending in {defaultMethod.last4}
                          </p>
                          <p className="text-sm text-gray-600">
                            Expires {defaultMethod.expMonth}/{defaultMethod.expYear}
                          </p>
                        </div>
                      </div>
                    ) : null;
                  })()}
                </div>
              )}

              {productSelector}
              <div className="flex justify-end">
                <button onClick={handleSaveProducts} disabled={processing || !selectedPriceIds.length} className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50">
                  {processing ? 'Saving...' : 'Save products'}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'payment' && (
            <CardManagement siteId={siteId} />
          )}

          {activeTab === 'billing' && (
            <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
              <Calendar className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Billing History</h3>
              <p className="text-gray-600">
                Invoice history will be displayed here once billing is active
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SubscriptionManagement;
